import { useState, useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { Formik, Form, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import Bugsnag from '@bugsnag/js'

import { Box, Typography, Icon } from '@mui/material'

import AdxInput from 'components/custom/AdxInput'
import AdxPhoneNumber from 'components/reusable/AdxPhoneNumber/AdxPhoneNumber'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'

import { handleValidationErrors, handleScroll } from 'services/helper/handleValidationErrors'
import { postPersonalData } from 'services/api/modules/staff-management/staffManagementDashboard'
import AdxButton from 'components/custom/AdxButton'

const relationShipList = [
  'Mother',
  'Father',
  'Spouse',
  'Partner',
  'Son',
  'Daughter',
  'Sibling',
  'Guardian',
  'Mortal',
  'Others',
]

const validationSchema = Yup.object({
  phone_no: Yup.string()
    .nullable()
    .required('Phone Number is required')
    .matches(/^[0-9\b]+$/, 'Phone Numbers can only contain numbers'),
  phone_country_id: Yup.string().nullable().required('Phone Code is required'),
  personal_email: Yup.string()
    .email('Invalid Email Address')
    .nullable()
    .required('Personal Email is required'),
  address: Yup.string().nullable().required('Home Address is required'),
  postcode: Yup.string()
    .nullable()
    .when('country', {
      is: 'Malaysia',
      then: Yup.string()
        .nullable()
        .matches(/^[0-9]{5}?$/, 'Postal Code must be in number, eg. 56000'),
      otherwise: Yup.string()
        .nullable()
        .matches(/^[a-z\d\s]+$/i, 'Postal Code must be alphanumeric, eg. S10 2GA'),
    })
    .required('Postal Code is required'),
  state: Yup.string().nullable().required('State is required'),
  country: Yup.string().nullable().required('Country is required'),
  emergency_contact_name: Yup.string()
    .nullable()
    .required('Emergency Contact Name is required')
    .matches(/^[a-zA-Z-@ /.']+$/, "Name must be valid eg. Alphabet with @, ', -, . and /"),
  emergency_contact_relationship: Yup.string().nullable().required('Relationship is required'),
  emergency_contact_phone_no: Yup.string()
    .nullable()
    .required('Phone Number is required')
    .matches(/^[0-9\b]+$/, 'Phone Numbers can only contain numbers'),
  emergency_contact_phone_country_id: Yup.string().nullable().required('Phone Code is required'),
})

const ContactDetails = ({ data, countriesList, employeeID }) => {
  const queryClient = useQueryClient()
  const formikRef = useRef()

  const [disable, setDisable] = useState(true)
  const [initialValues, setInitialValues] = useState({
    phone_country_id: '',
    phone_no: '',
    personal_email: '',
    address: '',
    postcode: '',
    state: '',
    country: '',
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_phone_country_id: '',
    emergency_contact_phone_no: '',
  })

  useEffect(() => {
    if (data) {
      setInitialValues({
        phone_country_id: data?.data?.phone_country_id ? data?.data?.phone_country_id : 132, // Default Malaysia
        phone_no: data?.data?.phone_no ? data?.data?.phone_no : '',
        personal_email: data?.data?.personal_email ? data?.data?.personal_email : '',
        address: data?.data?.address ? data?.data?.address : '',
        postcode: data?.data?.postcode ? data?.data?.postcode : '',
        state: data?.data?.state,
        country: data?.data?.country ? data?.data?.country : 'Malaysia', // Default Malaysia
        emergency_contact_name: data?.data?.emergency_contact_name
          ? data?.data?.emergency_contact_name
          : '',
        emergency_contact_relationship: data?.data?.emergency_contact_relationship,
        emergency_contact_phone_country_id: data?.data?.emergency_contact_phone_country_id
          ? data?.data?.emergency_contact_phone_country_id
          : 132, // Default Malaysia
        emergency_contact_phone_no: data?.data?.emergency_contact_phone_no
          ? data?.data?.emergency_contact_phone_no
          : '',
      })
    }
  }, [data])

  const cancelEditForm = (resetForm) => {
    resetForm()
    setDisable(true)
  }
  const mutation = useMutation((value) => postPersonalData(data?.data?.id, value), {
    onSuccess: () => {
      queryClient.invalidateQueries(['detailedEmployeeData', employeeID])
      queryClient.invalidateQueries(['employeePersonalData', employeeID])
      window.scrollTo(0, 0)
      setDisable(true)
    },
    onError: (error) => {
      if (error.status === 422) {
        handleValidationErrors(error, formikRef)
        if (error.data.errors) {
          handleScroll(error.data.errors)
        }
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })
  const handleSubmitContact = (values, actions) => {
    mutation.mutate(values)
  }

  const handleCheckRef = async (handleFormikSubmit) => {
    await handleFormikSubmit()
    const { errors = {} } = formikRef.current
    handleScroll(errors)
  }

  return (
    <Formik
      onSubmit={handleSubmitContact}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      innerRef={formikRef}
    >
      {({ values, touched, errors, handleChange, handleSubmit, setFieldValue, resetForm }) => (
        <Form>
          <Box
            p={1.75}
            display="flex"
            justifyContent="space-between"
            borderBottom="1px solid"
            sx={{ borderColor: 'gray-line.main' }}
            alignItems="center"
          >
            <Typography color="light-gray-text.main" fontWeight={500}>
              Contact details
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              px: 2,
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'gray-line.main',
            }}
          >
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              mt={2}
              width={'100%'}
              sx={{ rowGap: 1 }}
            >
              <Box sx={{ width: { xs: '100%', sm: '40%' }, columnGap: 2 }}>
                <AdxPhoneNumber
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  inputDisabled={disable}
                  dropdownDisabled={disable}
                  label="Phone Number"
                  placeholder="E.g: 123456789"
                  dropdownValue={values.phone_country_id}
                  dropdownName="phone_country_id"
                  onDropdownChange={(value) => setFieldValue(`phone_country_id`, value)}
                  inputValue={values.phone_no}
                  inputName="phone_no"
                  onInputChange={handleChange}
                  dropdownErrorMessage={
                    touched.phone_country_id && errors.phone_country_id && errors.phone_country_id
                  }
                  inputErrorMessage={touched.phone_no && errors.phone_no && errors.phone_no}
                  dropdownIsErrorShown={touched.phone_country_id && errors.phone_country_id}
                  inputIsErrorShown={touched.phone_no && errors.phone_no}
                />
              </Box>
              <Box sx={{ width: { xs: 1, sm: '60%' }, ml: { xs: 0, sm: 2 } }}>
                <AdxInput
                  label="Personal Email"
                  disabled={disable}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Personal Email"
                  value={values.personal_email}
                  name="personal_email"
                  onChange={handleChange}
                  sx={{ width: 1 }}
                  errorMessage={<ErrorMessage name="personal_email" component={AdxErrorMessage} />}
                  isErrorShown={touched.personal_email && errors.personal_email}
                />
              </Box>
            </Box>
            <Box>
              <AdxInput
                label="Home Address"
                disabled={disable}
                sxLabel={{
                  color: disable ? 'light-gray-text.main' : 'dark-text.main',
                }}
                sxInput={{
                  color: 'light-gray-text.main',
                  backgroundColor: 'white.main',
                }}
                size="standard"
                placeholder="Home Address"
                value={values.address}
                name="address"
                onChange={handleChange}
                sx={{ width: '100%', mb: 2.5 }}
                multiline
                minRows={3}
                errorMessage={<ErrorMessage name="address" component={AdxErrorMessage} />}
                isErrorShown={touched.address && errors.address}
              />
            </Box>
            <Box gap={2} display="flex" justifyContent="space-between" alignItems="center">
              <Box sx={{ width: 1 / 3 }}>
                <AdxInput
                  disabled={disable}
                  label="Postal code"
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Postal code"
                  value={values.postcode}
                  name="postcode"
                  onChange={handleChange}
                  sx={{ flex: '1' }}
                  errorMessage={<ErrorMessage name="postcode" component={AdxErrorMessage} />}
                  isErrorShown={touched.postcode && errors.postcode}
                />
              </Box>

              {/* State */}
              <Box sx={{ width: 1 / 3 }}>
                <AdxDropdown
                  placeholder="State"
                  label="State"
                  disabled={disable ? true : values.country ? false : true}
                  sx={{ flex: '1', minWidth: '100px' }}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxDropdown={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  value={values.state}
                  name="state"
                  onChange={handleChange}
                  errorMessage={<ErrorMessage name="state" component={AdxErrorMessage} />}
                  isErrorShown={touched.state && errors.state}
                >
                  {countriesList &&
                    values.country &&
                    countriesList.length !== 0 &&
                    countriesList
                      ?.find((node) => node.name === values.country)
                      ?.states.map((item) => (
                        <AdxMenuItem value={item.name} selectedvalue={values.state}>
                          {item.name}
                        </AdxMenuItem>
                      ))}
                </AdxDropdown>
              </Box>
              {/* Country */}
              <Box sx={{ width: 1 / 3 }}>
                <AdxDropdown
                  placeholder="Country"
                  label="Country"
                  disabled={disable}
                  sx={{ flex: '1', minWidth: '100px' }}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxDropdown={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  value={values.country}
                  name="country"
                  onChange={(event) => {
                    setFieldValue(`country`, event.target.value)
                    setFieldValue(`state`, null)
                  }}
                  errorMessage={<ErrorMessage name="country" component={AdxErrorMessage} />}
                  isErrorShown={touched.country && errors.country}
                >
                  {countriesList &&
                    countriesList.length !== 0 &&
                    countriesList
                      .filter((cc) => cc.country_code !== 'IL')
                      .map((item) => (
                        <AdxMenuItem value={item.name} selectedvalue={values.country}>
                          {item.name}
                        </AdxMenuItem>
                      ))}
                </AdxDropdown>
              </Box>
            </Box>
            <Box>
              <AdxInput
                disabled={disable}
                label="Emergency contact name"
                sxLabel={{
                  color: disable ? 'light-gray-text.main' : 'dark-text.main',
                }}
                sxInput={{
                  color: 'light-gray-text.main',
                  backgroundColor: 'white.main',
                }}
                size="standard"
                placeholder="Emergency contact name"
                value={values.emergency_contact_name}
                name="emergency_contact_name"
                onChange={handleChange}
                sx={{ width: '100%', mb: 2.5 }}
                errorMessage={
                  <ErrorMessage name="emergency_contact_name" component={AdxErrorMessage} />
                }
                isErrorShown={touched.emergency_contact_name && errors.emergency_contact_name}
              />
            </Box>
            <Box width={'100%'} gap={2} display="flex" flexDirection={{ xs: 'column', sm: 'row' }}>
              <Box width={{ xs: 1, sm: '40%' }}>
                {/* Relationship */}
                <AdxDropdown
                  placeholder="Relationship"
                  label="Relationship"
                  disabled={disable}
                  sx={{ width: '100%' }}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxDropdown={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  value={values.emergency_contact_relationship}
                  name="emergency_contact_relationship"
                  onChange={handleChange}
                  errorMessage={
                    <ErrorMessage
                      name="emergency_contact_relationship"
                      component={AdxErrorMessage}
                    />
                  }
                  isErrorShown={
                    touched.emergency_contact_relationship && errors.emergency_contact_relationship
                  }
                >
                  {relationShipList.map((rel) => (
                    <AdxMenuItem value={rel} selectedvalue={values.emergency_contact_relationship}>
                      {rel}
                    </AdxMenuItem>
                  ))}
                </AdxDropdown>
              </Box>
              <Box width={{ xs: 1, sm: '60%' }}>
                <AdxPhoneNumber
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  inputDisabled={disable}
                  dropdownDisabled={disable}
                  label="Phone Number"
                  placeholder="E.g: 123456789"
                  sxInput={{ width: '100%' }}
                  dropdownValue={values.emergency_contact_phone_country_id}
                  dropdownName="emergency_contact_phone_country_id"
                  onDropdownChange={(value) =>
                    setFieldValue(`emergency_contact_phone_country_id`, value)
                  }
                  inputValue={values.emergency_contact_phone_no}
                  inputName="emergency_contact_phone_no"
                  onInputChange={handleChange}
                  dropdownErrorMessage={
                    touched.emergency_contact_phone_country_id &&
                    errors.emergency_contact_phone_country_id &&
                    errors.emergency_contact_phone_country_id
                  }
                  inputErrorMessage={
                    touched.emergency_contact_phone_no &&
                    errors.emergency_contact_phone_no &&
                    errors.emergency_contact_phone_no
                  }
                  dropdownIsErrorShown={
                    touched.emergency_contact_phone_country_id &&
                    errors.emergency_contact_phone_country_id
                  }
                  inputIsErrorShown={
                    touched.emergency_contact_phone_no && errors.emergency_contact_phone_no
                  }
                />
              </Box>
            </Box>
            {!disable && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  columnGap: 1,
                }}
              >
                <AdxButton
                  variant={'outlined'}
                  onClick={() => cancelEditForm(resetForm)}
                  sx={{ width: 0.2 }}
                >
                  Cancel
                </AdxButton>
                <AdxButton
                  variant={'contained'}
                  onClick={() => handleCheckRef(handleSubmit)}
                  sx={{ width: 0.2 }}
                >
                  Save
                </AdxButton>
              </Box>
            )}
          </Box>
          {disable && (
            <Box sx={{ p: 0.5 }}>
              <AdxButton
                variant={'ghost'}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                }}
                onClick={() => setDisable(false)}
              >
                <Icon sx={{ fontSize: '15px', color: 'alternate.main' }}>edit</Icon>
                <Typography fontWeight={500} color="alternate.main" ml={1.25}>
                  Edit contact details
                </Typography>
              </AdxButton>
            </Box>
          )}
        </Form>
      )}
    </Formik>
  )
}

export default ContactDetails
